import { CreditCardData } from '../components/ui/EnhancedCreditCard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface CreateCardData {
  name: string;
  type: 'platinum' | 'gold' | 'black';
  number: string;
  limit: number;
  expiry: string;
  apr: number;
}

interface CardRewards {
  card_id: string;
  total_points: number;
  available_points: number;
  tier: string;
  cashback_rate: number;
  redemption_options: {
    id: string;
    name: string;
    points_required: number;
    value: number;
    category: string;
  }[];
}

interface SpendingInsight {
  period: string;
  total_spent: number;
  category_breakdown: {
    category: string;
    amount: number;
    percentage: number;
  }[];
  top_merchants: {
    merchant: string;
    amount: number;
    transaction_count: number;
  }[];
  trends: {
    date: string;
    amount: number;
  }[];
}

class CardAPI {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
        ...options,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('CardAPI request failed:', error);
      throw error;
    }
  }

  async getCards(): Promise<CreditCardData[]> {
    const response = await this.request<CreditCardData[]>('/cards');

    return response.data || [];
  }

  async getCard(cardId: string): Promise<CreditCardData | null> {
    const response = await this.request<CreditCardData>(`/cards/${cardId}`);

    return response.data || null;
  }

  async addCard(cardData: CreateCardData): Promise<CreditCardData> {
    const response = await this.request<CreditCardData>('/cards', {
      method: 'POST',
      body: JSON.stringify(cardData),
    });

    return response.data!;
  }

  async updateCard(cardId: string, updates: Partial<CreditCardData>): Promise<CreditCardData> {
    const response = await this.request<CreditCardData>(`/cards/${cardId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    return response.data!;
  }

  async freezeCard(cardId: string): Promise<CreditCardData> {
    const response = await this.request<CreditCardData>(`/cards/${cardId}/freeze`, {
      method: 'POST',
    });

    return response.data!;
  }

  async unfreezeCard(cardId: string): Promise<CreditCardData> {
    const response = await this.request<CreditCardData>(`/cards/${cardId}/unfreeze`, {
      method: 'POST',
    });

    return response.data!;
  }

  async getCardRewards(cardId: string): Promise<CardRewards> {
    const response = await this.request<CardRewards>(`/cards/${cardId}/rewards`);

    return response.data!;
  }

  async getSpendingInsights(cardId: string, period: string = 'month'): Promise<SpendingInsight> {
    const response = await this.request<SpendingInsight>(`/cards/${cardId}/insights?period=${period}`);

    return response.data!;
  }
}

export const cardAPI = new CardAPI();
export type { CreateCardData, CardRewards, SpendingInsight };
